import { useState, useEffect, useRef } from 'react'
import {
  ARCHIVE_REASON_CODES,
  ARCHIVE_REASON_LABELS,
  REJECTION_REASON_CODES,
  REJECTION_REASON_LABELS,
  requiresComment,
} from '@crm/shared'

interface Props {
  /** Заголовок — по умолчанию «Причина отказа». */
  title?: string
  /** Текст кнопки подтверждения. */
  confirmLabel?: string
  /** Какой список причин показывать (19.09.2026): отказ или архив. */
  kind?: 'rejection' | 'archive'
  /** Коллбек подтверждения. Код причины — из списка, reason — комментарий. */
  onConfirm: (reason: string, reasonCode: string) => Promise<void> | void
  /** Закрытие без сохранения. */
  onCancel: () => void
}

/**
 * Модалка обязательного ввода причины при закрытии заявки отказом.
 *
 * Используется из двух точек:
 *   - StatusEditor (dropdown в шапке заявки) — когда менеджер выбирает статус
 *     «Отказ» из списка.
 *   - ActionsPanel — кнопка «Отказ ✗» в блоке действий при статусе QUOTE_SENT.
 *
 * Кнопка «Сохранить» disabled пока поле пустое. Enter — сабмитит, Esc — отмена.
 */
export function RejectionReasonModal({
  title = 'Причина отказа',
  confirmLabel = 'Сохранить',
  kind = 'rejection',
  onConfirm,
  onCancel,
}: Props) {
  // Список причин (19.09.2026): раньше менеджер писал свободный текст, и одно
  // и то же в базе выглядело десятком формулировок — статистику было не
  // собрать. Комментарий остался, для «Другое» он обязателен.
  const codes = kind === 'archive' ? ARCHIVE_REASON_CODES : REJECTION_REASON_CODES
  const labels: Record<string, string> =
    kind === 'archive' ? ARCHIVE_REASON_LABELS : REJECTION_REASON_LABELS
  const [code, setCode] = useState<string>('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  // Esc — отмена
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onCancel])

  const commentRequired = !code || requiresComment(code)
  const canSubmit = !!code && (!commentRequired || !!reason.trim())

  const handleConfirm = async () => {
    const trimmed = reason.trim()
    if (!canSubmit) return
    setSaving(true)
    setError('')
    try {
      await onConfirm(trimmed, code)
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Не удалось сохранить'
      setError(msg)
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel()
      }}
    >
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <button
            onClick={onCancel}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500">
            {kind === 'archive'
              ? 'Выберите причину — она попадёт в аналитику. Комментарий виден всем в шапке заявки.'
              : 'Выберите причину отказа — она попадёт в аналитику. Комментарий виден всем в шапке заявки.'}
          </p>
          <div className="space-y-1">
            {codes.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="radio"
                  name="close-reason"
                  value={c}
                  checked={code === c}
                  onChange={() => setCode(c)}
                  className="text-brand-600"
                />
                {labels[c]}
              </label>
            ))}
          </div>
          <textarea
            ref={textareaRef}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                void handleConfirm()
              }
            }}
            rows={5}
            maxLength={2000}
            placeholder={commentRequired ? 'Комментарий обязателен' : 'Комментарий (необязательно)'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-y min-h-[100px]"
          />
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>{reason.length}/2000</span>
            <span>Ctrl/⌘ + Enter — сохранить</span>
          </div>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={() => void handleConfirm()}
            disabled={!canSubmit || saving}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Сохранение…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
